import { EChartService } from '@/service/echart/echart.service';
import { OrganizationCompetencyScoreDistribution, TargetCompetencyScore } from '@/models/student/student-competency-data';
import i18n from '@/i18n';

export class CompetencyDistributionEchartService extends EChartService {
  xAxisLabels: string[];
  height: number;
  showTargetBorder: boolean;
  organizationBorderColor: string;
  target: TargetCompetencyScore;
  organization: OrganizationCompetencyScoreDistribution;

  private readonly xAIXS_MIN = 0;
  private readonly xAIXS_MAX = 100;
  private readonly TARGET_COLOR = '#5CADFF';
  private readonly LINE_COLOR = '#ED4014';

  constructor(height: number, target: TargetCompetencyScore, organization: OrganizationCompetencyScoreDistribution, showTargetBorder: boolean = true) {
    super();
    this.series = [];
    this.height = height;
    this.showTargetBorder = showTargetBorder;
    this.target = target;
    this.organization = organization;
    this.organizationBorderColor = showTargetBorder ? '#E3A79D' : 'grey';
    // 後でチャートを追加する用途で本オブジェクトを使用しないため、生成時にseriesも作る
    this.addSeries();
  }

  /**
   * AIGROW2-184 箱ひげ図にデータテーブルを追加
   * 箱ひげ図のみにデータを表形式で表示する
   * EChartService が箱ひげ図以外のグラフでも共通なので、空配列を返すだけのものを追加しています
   * subTableColmuns
   * subTableData
   */
  get subTableColmuns(): any[] {
    return [];
  }
  get subTableData(): any[] {
    return [];
  }

  get options(): any {
    return {
      title: { left: 'center', top: -5 },
      legend: {
        data: [this.organization.name, this.target.fullName],
        top: 0,
      },
      tooltip: {
        axisPointer: {
          type: 'shadow',
        },
        formatter: function(params: any) {
          if (params.data.length !== 3) {
            return;
          }
          return `${params.data[0]}~${params.data[1]}：${params.data[2]}名`;
        },
      },
      grid: { width: '75%', height: '70%', top: 30, bottom: 0, right: 50 },
      xAxis: [{ type: 'value', name: i18n.t('SCORE').toString(), min: this.xAIXS_MIN, max: this.xAIXS_MAX }],
      yAxis: [
        {
          type: 'value',
          name: i18n.t('NUMBER_OF_PEOPLE').toString(),
          min: 0,
          max: this.yAxisMax,
          splitNumber: this.splitNumber,
        },
      ],
      series: this.series,
    };
  }

  addSeries() {
    // 組織ラインデータ
    const organizationLineData = this.organization.data.map((d: any) => {
      return [d[0] + 10, d[2]];
    });

    this.series.push({
      name: this.target.fullName,
      type: 'custom',
      renderItem: this.targetRender,
      itemStyle: { color: this.TARGET_COLOR },
      data: [{ value: [this.target.data, this.yAxisMax] }],
      z: 2,
    });
    this.series.push({
      name: this.organization.name,
      type: 'custom',
      renderItem: this.organizationRender,
      itemStyle: { opacity: 0.7, color: this.organizationBorderColor },
      data: this.organization.data,
      z: 1,
      formatter: function(data: any): string {
        return `${data}`;
      },
    });
    this.series.push({
      name: this.organization.name,
      type: 'line',
      smooth: true,
      itemStyle: { color: this.LINE_COLOR },
      data: organizationLineData,
    });
  }

  organizationRender(_: any, api: any) {
    // 人数統計
    const count = api.value(2);
    // グラフ上の開始位置計算
    const start = api.coord([api.value(0), count]);
    // グラフサイズ計算
    const size = api.size([api.value(1) - api.value(0), count]);
    // 棒グラフ情報作成
    return {
      type: 'rect',
      shape: { x: start[0] + 1, y: start[1], width: size[0] - 1, height: size[1] },
      style: api.style(),
    };
  }

  targetRender(params: any, api: any) {
    // 開始位置の計算
    const countMax = api.value(1);
    const start = api.coord([api.value(0), countMax]);
    //個人のグラフの幅
    const TARGET_BAR_WIDTH = 3;

    return {
      type: 'rect',
      shape: {
        x: start[0] - TARGET_BAR_WIDTH,
        y: start[1],
        width: TARGET_BAR_WIDTH,
        height: api.getHeight() * 0.7,
      },
      style: api.style(),
    };
  }

  get yAxisMax(): number {
    return Math.ceil(this.countMax / this.splitNumber) * this.splitNumber;
  }

  get splitNumber(): number {
    // データの件数が5件未満の時にはその値に合わせた軸数にする
    return this.countMax < 5 ? this.countMax : 5;
  }

  get countMax(): number {
    return Math.max.apply(
      null,
      this.organization.data.map((d: any) => d[2])
    );
  }

  setXAxisLabel(xAxisLabels: string[]) {
    this.xAxisLabels = xAxisLabels;
  }
}
